/**
 * @fileoverview Shared policy for replaying stored `role: 'user'` messages in
 * the UI — used by both the live session-resume path (ChatManager.loadHistoryMessages)
 * and the read-only transcript renderer (SessionMessageRenderer).
 *
 * Several user-role rows are persisted only to satisfy Anthropic's
 * user/assistant role alternation or to drive the agent's next turn after a
 * button click; they are internal signals, not real user content, and must
 * never surface as chat bubbles. See backend services/sessions/messageFilter.js
 * for the authoritative taxonomy.
 *
 * We intentionally do NOT use the strict `userAuthored` allowlist here: that
 * flag postdates older sessions, so a positive-only filter would erase genuine
 * typed turns from historical conversations. Instead we hide the known internal
 * markers, with a content fallback for legacy rows that predate the metadata
 * tags.
 *
 * @module chat/messageReplayPolicy
 */

/**
 * True when a user-message body is a synthetic internal marker rather than
 * something a human typed. Fallback for legacy rows stored before the metadata
 * signals existed. Conservative: only the well-known bracket markers a user
 * would never author.
 *
 * @param {string} content
 * @returns {boolean}
 */
export function isInternalMarkerContent(content) {
  if (typeof content !== 'string') return false;
  return /^\[(Action: |User selected: )/.test(content)
    || /^\[(begin|resume)\]$/.test(content.trim());
}

/**
 * @typedef {Object} UserReplayDecision
 * @property {boolean} skip - When true, the message must not be rendered.
 * @property {string} [text] - The text to display (present when skip is false).
 * @property {boolean} [isEcho] - True when this is an echoed button choice
 *   (callers may style it, e.g. `message--button-echo`).
 * @property {string} [dividerAfter] - Optional divider text to render directly
 *   after the shown user bubble.
 */

/**
 * Decide how to replay a stored `role: 'user'` message.
 *
 * Callers must check `message.role === 'user'` first; assistant/system messages
 * are not handled here.
 *
 * Hidden: systemTrigger / discoveryEscalation markers; LLM tool-call markers
 *   (`[Action: …]`, metadata.type === 'llm_action'); non-echo button clicks
 *   (`[User selected: …]`); legacy `[begin]`/`[resume]`/bracket sentinels.
 * Shown: echo button clicks (as their natural label); everything else (typed text).
 *
 * @param {Object} message - Message record ({ content, metadata }).
 * @returns {UserReplayDecision}
 */
export function classifyUserReplayMessage(message) {
  const meta = message?.metadata || {};
  const dividerAfter = meta.source === 'topic_transition'
    ? 'Discussing New Topic'
    : undefined;

  // Internal signals — never real user content.
  if (meta.systemTrigger || meta.discoveryEscalation) return { skip: true };
  if (meta.type === 'llm_action') return { skip: true };

  // Button-interaction records: replay only echo-flagged clicks as the natural
  // label; skip the rest (e.g. "[User selected: Begin questionnaire]").
  if (meta.type === 'button_click') {
    if (!meta.echo) return { skip: true };
    return {
      skip: false,
      text: meta.echoText || meta.buttonLabel || message?.content,
      isEcho: true,
      ...(dividerAfter && { dividerAfter })
    };
  }

  // Backward-compat for legacy rows that predate the metadata tags above.
  if (isInternalMarkerContent(message?.content)) return { skip: true };

  return {
    skip: false,
    text: message?.content,
    isEcho: false,
    ...(dividerAfter && { dividerAfter })
  };
}
